import { ref, onMounted, readonly, computed } from "vue";
import { shopZoneListApi, type ShopZone, ResponseCode } from "@src/api";
import { ShopZone as ShopZoneModel } from "@src/model";
import { normalize } from "@src/utils";

export type ShopZoneWithChildren = ShopZone & {
  subTitle?: string;
  children?: ShopZoneWithChildren[];
  isDepartment?: boolean;
  parentId?: string;
};

const shopZones = ref<{
  byId: Record<string, ShopZoneWithChildren>;
  allIds: string[];
}>({ byId: {}, allIds: [] });

export const useShopZoneHook = () => {
  const getShopZones = async () => {
    const { code, shop_zones } = await shopZoneListApi();

    if (code !== ResponseCode.OK || shop_zones.length === 0) return;

    const normalized = normalize<ShopZoneWithChildren, "zone_id">(shop_zones, "zone_id");

    for (let i = 0; i < shop_zones.length; i++) {
      const current = normalized.map[shop_zones.at(i).zone_id];
      const hierarchy = current.hierarchy.split(".");
      const prev = normalized.map[hierarchy.at(-2)];

      if (prev) {
        current.parentId = prev.zone_id;
        current.subTitle = ShopZoneModel.getSubTitle(current.hierarchy, (id) => normalized.map[id]?.title);
      } else {
        continue;
      }

      if (prev.children) {
        prev.children.push(current);
      } else {
        prev.children = [current];
      }
    }

    shopZones.value = { byId: normalized.map, allIds: normalized.ids };
  };

  const shopDepartments = computed(() => {
    return Object.values(shopZones.value.byId).filter((item) => !item.parentId);
  });

  const getZoneById = (id: string) => {
    return shopZones.value.byId[id];
  };

  onMounted(async () => {
    if (shopZones.value.allIds.length > 0) return;

    await getShopZones();
  });

  return {
    shopZones: readonly(shopZones),
    shopDepartments,
    getZoneById,
  };
};
